import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
    Shield,
    Search,
    Plus,
    X,
    CheckCircle,
    AlertCircle,
    Loader2,
    FileText,
    User,
    Building,
    Phone,
    Mail,
    Fingerprint,
    ChevronRight,
    Truck,
    Globe,
    MapPin,
    Calendar,
    DollarSign,
    Award,
    FileCheck,
    Download,
    Eye,
    ExternalLink
} from 'lucide-react';
import api from '../../services/api';

export default function IDFInsuranceCheck() {
    const { user } = useSelector((state) => state.auth);
    const [idfNumber, setIdfNumber] = useState('');
    const [selectedAgentPin, setSelectedAgentPin] = useState('');
    const [clearingAgents, setClearingAgents] = useState([]);
    const [loadingAgents, setLoadingAgents] = useState(true);
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState('');
    const [showAddAgent, setShowAddAgent] = useState(false);
    const [newAgent, setNewAgent] = useState({
        agent_name: '', kra_pin: '', company_name: '', phone: '', email: ''
    });
    const [savingAgent, setSavingAgent] = useState(false);
    const [agentError, setAgentError] = useState('');
    const [agentSuccess, setAgentSuccess] = useState('');

    useEffect(() => {
        fetchClearingAgents();
    }, []);

    const fetchClearingAgents = async () => {
        setLoadingAgents(true);
        try {
            const { data } = await api.get('/clearing-agents');
            setClearingAgents(data);
            if (data.length > 0 && !selectedAgentPin) {
                setSelectedAgentPin(data[0].kra_pin);
            }
        } catch (err) {
            console.error('Failed to load clearing agents');
        } finally {
            setLoadingAgents(false);
        }
    };

    const handleSaveAgent = async (e) => {
        e.preventDefault();
        if (!newAgent.agent_name || !newAgent.kra_pin) {
            setAgentError('Agent name and KRA PIN are required');
            return;
        }

        const kraPinRegex = /^[A-Z]\d{9}[A-Z]$/;
        if (!kraPinRegex.test(newAgent.kra_pin.toUpperCase())) {
            setAgentError('KRA PIN must be in format: A123456789B');
            return;
        }

        setSavingAgent(true);
        setAgentError('');
        setAgentSuccess('');
        try {
            await api.post('/clearing-agents', newAgent);
            setAgentSuccess('Clearing agent saved successfully!');
            setNewAgent({ agent_name: '', kra_pin: '', company_name: '', phone: '', email: '' });
            await fetchClearingAgents();
            setTimeout(() => {
                setShowAddAgent(false);
                setAgentSuccess('');
            }, 1500);
        } catch (err) {
            setAgentError(err.response?.data?.error || 'Failed to save agent');
        } finally {
            setSavingAgent(false);
        }
    };

    const handleCheck = async (e) => {
        e.preventDefault();
        if (!idfNumber.trim()) {
            setError('Please enter an IDF number');
            return;
        }
        if (!selectedAgentPin.trim()) {
            setError('Please select a clearing agent PIN');
            return;
        }
        setLoading(true);
        setError('');
        setResult(null);
        try {
            const { data } = await api.post('/cargo/idf-check-insurance', {
                idf_number: idfNumber.trim(),
                agents_company_pin: selectedAgentPin.trim()
            });
            setResult(data);
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to check IDF status');
        } finally {
            setLoading(false);
        }
    };

    const handleCancelAddAgent = () => {
        setShowAddAgent(false);
        setAgentError('');
        setAgentSuccess('');
        setNewAgent({ agent_name: '', kra_pin: '', company_name: '', phone: '', email: '' });
    };

    const isInsured = result?.body?.certificateResponse?.certificate_number;
    const idfData = result?.body?.idfResponseData;
    const certData = result?.body?.certificateResponse;

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-slate-900 dark:to-slate-800">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
                {/* Header */}
                <div className="relative bg-gradient-to-br from-[#1e3b29] via-[#3a663f] to-[#4d8c54] rounded-2xl p-8 md:p-10 text-white overflow-hidden shadow-2xl">
                    <div className="absolute top-0 right-0 w-96 h-96 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/3" />
                    <div className="absolute bottom-0 left-0 w-72 h-72 bg-white/10 rounded-full translate-y-1/3 -translate-x-1/3" />
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-white/5 rounded-full blur-3xl" />

                    <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                        <div>
                            <div className="flex items-center gap-2 mb-2">
                                <Shield className="w-5 h-5 text-green-300" />
                                <span className="text-green-200 text-xs uppercase tracking-widest font-semibold">Insurance Check</span>
                            </div>
                            <h1 className="text-3xl md:text-4xl font-extrabold">
                                Check IDF Insurance Status
                            </h1>
                            <p className="text-green-100/80 mt-2 max-w-2xl">
                                Verify whether an IDF has already been insured and view certificate details in real-time.
                            </p>
                        </div>
                        <div className="flex items-center gap-3">
                            <div className="px-4 py-2 bg-white/20 backdrop-blur-sm rounded-lg text-sm">
                                <span className="opacity-60">Status</span>
                                <span className="ml-2 font-medium">IDF Check</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Search Form */}
                <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl border border-gray-200 dark:border-slate-700 p-6 md:p-8">
                    <form onSubmit={handleCheck} className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                            {/* IDF Number */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1.5">
                                    IDF Number <span className="text-red-500">*</span>
                                </label>
                                <div className="relative">
                                    <div className="absolute left-3 top-1/2 -translate-y-1/2">
                                        <FileText className="w-5 h-5 text-gray-400" />
                                    </div>
                                    <input
                                        type="text"
                                        value={idfNumber}
                                        onChange={(e) => setIdfNumber(e.target.value)}
                                        placeholder="e.g., 26MBAIM000010821"
                                        className="w-full pl-10 pr-4 py-3.5 border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-white rounded-xl focus:ring-2 focus:ring-[#3a663f]/50 focus:border-[#3a663f] outline-none transition-all hover:shadow-sm text-lg font-mono"
                                        required
                                    />
                                </div>
                            </div>

                            {/* Clearing Agent */}
                            <div>
                                <div className="flex items-center justify-between mb-1.5">
                                    <label className="block text-sm font-medium text-gray-700 dark:text-slate-300">
                                        Clearing Agent PIN <span className="text-red-500">*</span>
                                    </label>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            if (showAddAgent) {
                                                handleCancelAddAgent();
                                            } else {
                                                setShowAddAgent(true);
                                                setAgentError('');
                                                setAgentSuccess('');
                                            }
                                        }}
                                        className={`text-xs font-medium flex items-center gap-1 transition-colors ${showAddAgent
                                                ? 'text-red-500 hover:text-red-600 dark:text-red-400 dark:hover:text-red-300'
                                                : 'text-[#3a663f] dark:text-green-400 hover:text-[#2d5231] dark:hover:text-green-300'
                                            }`}
                                    >
                                        {showAddAgent ? (
                                            <>
                                                <X className="w-3.5 h-3.5" />
                                                Cancel
                                            </>
                                        ) : (
                                            <>
                                                <Plus className="w-3.5 h-3.5" />
                                                Add New
                                            </>
                                        )}
                                    </button>
                                </div>
                                {loadingAgents ? (
                                    <div className="h-12 bg-gray-100 dark:bg-slate-700 rounded-xl animate-pulse" />
                                ) : clearingAgents.length > 0 ? (
                                    <div className="relative">
                                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                                        <select
                                            value={selectedAgentPin}
                                            onChange={(e) => setSelectedAgentPin(e.target.value)}
                                            className="w-full pl-10 pr-10 py-3.5 border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-white rounded-xl focus:ring-2 focus:ring-[#3a663f]/50 focus:border-[#3a663f] outline-none transition-all hover:shadow-sm appearance-none"
                                            required
                                        >
                                            <option value="">-- Select a clearing agent --</option>
                                            {clearingAgents.map((agent) => (
                                                <option key={agent.id} value={agent.kra_pin}>
                                                    {agent.agent_name} - {agent.kra_pin}
                                                    {agent.company_name ? ` (${agent.company_name})` : ''}
                                                </option>
                                            ))}
                                        </select>
                                        <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                                            <ChevronRight className="w-4 h-4 text-gray-400 rotate-90" />
                                        </div>
                                    </div>
                                ) : (
                                    <div className="relative">
                                        <Fingerprint className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                                        <input
                                            type="text"
                                            value={selectedAgentPin}
                                            onChange={(e) => setSelectedAgentPin(e.target.value.toUpperCase())}
                                            placeholder="e.g., A123456789B"
                                            className="w-full pl-10 pr-4 py-3.5 border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-white rounded-xl focus:ring-2 focus:ring-[#3a663f]/50 focus:border-[#3a663f] outline-none transition-all hover:shadow-sm font-mono uppercase"
                                            required
                                        />
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Add Agent Form */}
                        {showAddAgent && (
                            <div className="bg-gray-50 dark:bg-slate-700/30 rounded-xl p-4 border border-gray-200 dark:border-slate-600 animate-fadeIn">
                                <div className="flex items-center gap-2 mb-3">
                                    <User className="w-4 h-4 text-[#3a663f] dark:text-green-400" />
                                    <h4 className="font-semibold text-gray-800 dark:text-white text-sm">Add Clearing Agent</h4>
                                </div>

                                {agentError && (
                                    <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-3 py-2 rounded-lg text-xs flex items-center gap-2 mb-3">
                                        <AlertCircle className="w-3.5 h-3.5" />
                                        {agentError}
                                    </div>
                                )}
                                {agentSuccess && (
                                    <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-700 dark:text-green-400 px-3 py-2 rounded-lg text-xs flex items-center gap-2 mb-3">
                                        <CheckCircle className="w-3.5 h-3.5" />
                                        {agentSuccess}
                                    </div>
                                )}

                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                                    <input
                                        type="text"
                                        placeholder="Agent Name *"
                                        value={newAgent.agent_name}
                                        onChange={(e) => setNewAgent({ ...newAgent, agent_name: e.target.value })}
                                        className="border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-white rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#3a663f]/50 focus:border-[#3a663f] outline-none transition-all"
                                        required
                                    />
                                    <input
                                        type="text"
                                        placeholder="KRA PIN *"
                                        value={newAgent.kra_pin}
                                        onChange={(e) => setNewAgent({ ...newAgent, kra_pin: e.target.value.toUpperCase() })}
                                        className="border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-white rounded-lg px-3 py-2 text-sm font-mono uppercase focus:ring-2 focus:ring-[#3a663f]/50 focus:border-[#3a663f] outline-none transition-all"
                                        required
                                    />
                                    <input
                                        type="text"
                                        placeholder="Company Name"
                                        value={newAgent.company_name}
                                        onChange={(e) => setNewAgent({ ...newAgent, company_name: e.target.value })}
                                        className="border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-white rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#3a663f]/50 focus:border-[#3a663f] outline-none transition-all"
                                    />
                                    <button
                                        type="button"
                                        onClick={handleSaveAgent}
                                        disabled={savingAgent}
                                        className="bg-[#3a663f] hover:bg-[#2d5231] text-white rounded-lg px-4 py-2 text-sm font-medium transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                    >
                                        {savingAgent ? (
                                            <>
                                                <Loader2 className="w-4 h-4 animate-spin" />
                                                Saving...
                                            </>
                                        ) : (
                                            <>
                                                <Plus className="w-4 h-4" />
                                                Save Agent
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Error */}
                        {error && (
                            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 p-4 rounded-xl flex items-start gap-3 animate-fadeIn">
                                <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                                <span className="text-sm flex-1">{error}</span>
                                <button onClick={() => setError('')} className="text-red-500 hover:text-red-700">
                                    <X className="w-4 h-4" />
                                </button>
                            </div>
                        )}

                        {/* Submit */}
                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full bg-gradient-to-r from-[#2d5232] to-[#3a663f] hover:from-[#3a663f] hover:to-[#4d8c54] text-white font-semibold py-4 rounded-xl shadow-lg shadow-[#3a663f]/20 hover:shadow-xl transition-all duration-300 transform active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-3 text-lg"
                        >
                            {loading ? (
                                <>
                                    <Loader2 className="w-5 h-5 animate-spin" />
                                    Checking Insurance Status...
                                </>
                            ) : (
                                <>
                                    <Search className="w-5 h-5" />
                                    Check Insurance Status
                                </>
                            )}
                        </button>
                    </form>
                </div>

                {/* Results */}
                {result && (
                    <div className="space-y-6 animate-fadeIn">
                        {/* Status Banner */}
                        <div className={`rounded-2xl p-6 text-white shadow-xl ${isInsured
                                ? 'bg-gradient-to-r from-[#1e3b29] via-[#3a663f] to-[#4d8c54]'
                                : 'bg-gradient-to-r from-amber-500 to-amber-600'
                            }`}>
                            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                                <div>
                                    <div className="flex items-center gap-2">
                                        {isInsured ? (
                                            <CheckCircle className="w-6 h-6 text-green-300" />
                                        ) : (
                                            <AlertCircle className="w-6 h-6 text-amber-200" />
                                        )}
                                        <p className="text-sm opacity-90">IDF: {idfData?.idf_number}</p>
                                    </div>
                                    <h2 className="text-2xl font-extrabold mt-1">
                                        {isInsured ? '✅ Insured' : '❌ Not Insured'}
                                    </h2>
                                    {isInsured && (
                                        <p className="text-sm text-green-100/80 mt-1">
                                            This IDF has a valid insurance certificate
                                        </p>
                                    )}
                                </div>
                                {isInsured && certData?.certificate_number && (
                                    <div className="bg-white/20 backdrop-blur-sm px-4 py-3 rounded-xl text-center">
                                        <p className="text-xs opacity-75">Certificate Number</p>
                                        <p className="font-bold text-lg">{certData.certificate_number}</p>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* IDF Details */}
                        {idfData && (
                            <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl border border-gray-200 dark:border-slate-700 overflow-hidden">
                                <div className="p-6 border-b border-gray-200 dark:border-slate-700">
                                    <div className="flex items-center gap-2">
                                        <FileText className="w-5 h-5 text-[#3a663f] dark:text-green-400" />
                                        <h3 className="font-bold text-gray-800 dark:text-white">IDF Details</h3>
                                    </div>
                                </div>
                                <div className="p-6">
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                                        <Detail label="IDF Number" value={idfData.idf_number} highlight />
                                        <Detail label="UCR Number" value={idfData.ucr_number || 'N/A'} />
                                        <Detail label="Registration Date" value={idfData.registration_date_time} />
                                        <Detail label="Delivery Place" value={idfData.delivery_place} />
                                        <Detail label="Invoice Currency" value={idfData.invoice_currency} />
                                        <Detail label="MCI Status" value={idfData.mci_status} />
                                        <Detail label="Origin Country" value={idfData.origin_country} />
                                        <Detail label="Mode of Transport" value={idfData.mode_of_transport} />
                                        <Detail label="Total Customs Value" value={`${idfData.invoice_currency} ${Number(idfData.total_custom_values).toLocaleString()}`} />
                                        <Detail label="FOB Value" value={`${idfData.invoice_currency} ${Number(idfData.fob_value).toLocaleString()}`} />
                                    </div>

                                    {/* Consignee */}
                                    {idfData.consigenee?.[0] && (
                                        <div className="mt-4 pt-4 border-t border-gray-200 dark:border-slate-700">
                                            <p className="text-xs text-gray-400 dark:text-slate-500 uppercase tracking-wider mb-2">Consignee</p>
                                            <div className="bg-gray-50 dark:bg-slate-700/30 rounded-xl p-4">
                                                <p className="font-medium text-gray-800 dark:text-white">{idfData.consigenee[0].name}</p>
                                                <p className="text-sm text-gray-500 dark:text-slate-400">KRA PIN: {idfData.consigenee[0].importer_kra_pin}</p>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Certificate Details (if insured) */}
                        {certData && (
                            <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl border border-green-200 dark:border-green-800 overflow-hidden">
                                <div className="p-6 border-b border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/10">
                                    <div className="flex items-center gap-2">
                                        <Award className="w-5 h-5 text-green-600 dark:text-green-400" />
                                        <h3 className="font-bold text-green-800 dark:text-green-300">Insurance Certificate</h3>
                                        <span className="ml-2 text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 px-2 py-0.5 rounded-full">Active</span>
                                    </div>
                                </div>
                                <div className="p-6">
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                                        <Detail label="Certificate Number" value={certData.certificate_number} highlight />
                                        <Detail label="Insurer" value={certData.insurance_company_name} />
                                        <Detail label="Insurer KRA PIN" value={certData.insurance_company_kra_pin} />
                                        <Detail label="Insured Name" value={certData.name} />
                                        <Detail label="Basic Premium" value={certData.basic_premium} />
                                        <Detail label="Stamp Duty" value={certData.stamp_duty} />
                                        <Detail label="PHCF" value={certData.phcf} />
                                        <Detail label="Training Levy" value={certData.training_levy} />
                                        <Detail label="MCI" value={certData.mci} />
                                        <Detail label="Total Charges" value={certData.total_charges} highlight />
                                        <Detail label="Insured Amount" value={certData.insured_amount} />
                                        <Detail label="IRA Policy No" value={certData.ira_policy_no || 'N/A'} />
                                    </div>

                                    {/* Download Button */}
                                    {certData.certificate_url && (
                                        <div className="mt-6 pt-6 border-t border-gray-200 dark:border-slate-700 flex flex-wrap gap-3">
                                            <a
                                                href={certData.certificate_url}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="inline-flex items-center gap-2 bg-[#3a663f] hover:bg-[#2d5231] text-white font-medium py-2.5 px-6 rounded-xl transition-colors shadow-lg hover:shadow-xl"
                                            >
                                                <Download className="w-4 h-4" />
                                                Download Certificate
                                            </a>
                                            <a
                                                href={certData.certificate_url}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="inline-flex items-center gap-2 border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-slate-300 font-medium py-2.5 px-6 rounded-xl hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors"
                                            >
                                                <ExternalLink className="w-4 h-4" />
                                                View in Browser
                                            </a>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Policy Documents */}
                        {result?.body?.policyDocumentResponseDTO?.length > 0 && (
                            <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl border border-gray-200 dark:border-slate-700 overflow-hidden">
                                <div className="p-6 border-b border-gray-200 dark:border-slate-700">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-2">
                                            <FileCheck className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                                            <h3 className="font-bold text-gray-800 dark:text-white">Policy Documents</h3>
                                        </div>
                                        <span className="text-sm bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 px-2.5 py-0.5 rounded-full">
                                            {result.body.policyDocumentResponseDTO.length} document{result.body.policyDocumentResponseDTO.length !== 1 ? 's' : ''}
                                        </span>
                                    </div>
                                </div>
                                <div className="p-6 space-y-3">
                                    {result.body.policyDocumentResponseDTO.map((doc, idx) => (
                                        <div key={idx} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-slate-700/30 rounded-xl hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors">
                                            <div className="flex items-center gap-3">
                                                <div className="p-2 bg-blue-100 dark:bg-blue-900/20 rounded-lg">
                                                    <FileText className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                                                </div>
                                                <div>
                                                    <p className="font-medium text-gray-800 dark:text-white">{doc.description || `Document ${idx + 1}`}</p>
                                                    <p className="text-xs text-gray-400 dark:text-slate-500">
                                                        Created: {new Date(doc.createdAt).toLocaleDateString()}
                                                    </p>
                                                </div>
                                            </div>
                                            <span className="px-2.5 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-xs rounded-full">
                                                {doc.status || 'Available'}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Info Box */}
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4">
                    <div className="flex items-start gap-3">
                        <Shield className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                        <div>
                            <p className="text-sm font-medium text-blue-800 dark:text-blue-300">What does this check do?</p>
                            <p className="text-xs text-blue-700 dark:text-blue-400 mt-1">
                                This tool queries the insurance system to verify if an IDF already has an active insurance certificate.
                                It retrieves all policy details, including the insurer, premium amounts, and certificate documents.
                                Use this to avoid double-insuring shipments.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

// ========== SUB-COMPONENTS ==========

function Detail({ label, value, highlight }) {
    return (
        <div>
            <p className="text-xs text-gray-400 dark:text-slate-500 uppercase tracking-wider">{label}</p>
            <p className={`font-medium truncate ${highlight ? 'text-[#3a663f] dark:text-green-400 font-bold text-sm' : 'text-gray-800 dark:text-white'}`}>
                {value || 'N/A'}
            </p>
        </div>
    );
}